import type { JSONContent, Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import type { ProductSnapshot } from '@/types/product';
import { confirmAction } from '@/lib/confirm';
import { formatProductPrice, isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';
import { investmentProductDescriptionContent } from './investmentTableCellContent';
import {
  calculateInvestmentProduct,
  formatRange,
  investmentProductCatalogText,
  investmentProductDescriptionHtml,
  investmentProductRangeVariant,
  publicInvestmentProduct,
  resetInvestmentProduct,
  type InvestmentProduct,
} from '../../../backend/src/utils/productInvestment';
export {
  calculateInvestmentProduct,
  formatRange,
  investmentProductCatalogText,
  investmentProductDescriptionHtml,
  investmentProductRangeVariant,
  publicInvestmentProduct,
  resetInvestmentProduct,
};
export type { InvestmentProduct };

/** Added quote lines have no catalog value to restore. Pass rangeVariantId for the 'tot' side. */
export function investmentProductCatalogLine(
  product: InvestmentProduct,
  code: string,
  variantId = product.variantId
) {
  // Older quote snapshots identify added lines only by their generated code.
  if (code.startsWith('custom_')) return undefined;
  return (product.catalogVariants ?? product.variants)
    .find((variant) => variant.id === variantId)
    ?.lines.find((line) => line.code === code);
}

export function createInvestmentProduct(
  snapshot: ProductSnapshot,
  options: { quantity?: number; discountPct?: number; customPrice?: string; rangeVariantId?: string } = {}
): InvestmentProduct {
  const customPrice = options.customPrice?.trim();
  const keepsRange =
    options.rangeVariantId !== undefined &&
    options.rangeVariantId !== snapshot.variantId &&
    Boolean(snapshot.pricingVariants?.some((variant) => variant.id === options.rangeVariantId));
  return {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    productId: snapshot.productId,
    productVersionId: snapshot.productVersionId,
    productName: snapshot.productName,
    priceMode: snapshot.priceMode,
    variants: (
      snapshot.pricingVariants ?? [
        {
          id: snapshot.variantId,
          name: snapshot.variantName,
          priceCents: snapshot.priceCents,
          descriptionHtml: snapshot.descriptionHtml,
          lines: snapshot.lines.map((line) => ({
            ...line,
            rateCents: line.hours ? line.totalCents / line.hours : 0,
          })),
        },
      ]
    ).map((variant) => ({
      ...variant,
      ...(variant.id === snapshot.variantId &&
      variant.descriptionHtml === undefined &&
      snapshot.descriptionHtml
        ? { descriptionHtml: snapshot.descriptionHtml }
        : {}),
    })),
    variantId: snapshot.variantId,
    ...(keepsRange ? { rangeVariantId: options.rangeVariantId } : {}),
    display: 'collapsed',
    showHours: false,
    quantity: options.quantity ?? 1,
    discountPct: options.discountPct ?? 0,
    overrides: {},
    ...(snapshot.priceMode === 'on_request' && snapshot.onRequestText?.trim()
      ? { onRequestText: snapshot.onRequestText.trim() }
      : {}),
    ...(customPrice
      ? isCurrencyAmount(customPrice)
        ? { amountCents: Math.round(parseCurrencyAmount(customPrice) * 100) }
        : { amountText: customPrice }
      : {}),
  };
}

export function investmentProductRow(
  product: InvestmentProduct,
  pricingType: string,
  currency = 'EUR'
): JSONContent {
  const computed = calculateInvestmentProduct(product);
  const descriptionHtml = investmentProductDescriptionHtml(product);
  const amount = formatProductPrice(computed, currency);
  const texts =
    pricingType === 'hourly'
      ? [product.productName, '', '', amount]
      : pricingType === 'fixed'
        ? [product.productName]
        : [product.productName, amount];
  return {
    type: 'tableRow',
    attrs: {
      investmentProduct: product,
      productId: product.productId,
      productVersionId: product.productVersionId,
      variantId: product.variantId,
      quantity: product.quantity,
      discountPct: product.discountPct,
      hasOverrides: computed.adjusted,
    },
    content: texts.map((text, index) => ({
      type: 'tableCell',
      content:
        index === 0
          ? investmentProductDescriptionContent(product.productName, descriptionHtml)
          : [
              {
                type: 'paragraph',
                ...(text ? { content: [{ type: 'text', text }] } : {}),
              },
            ],
    })),
  };
}

export function findInvestmentProduct(editor: Editor, id: string) {
  let found: { pos: number; node: import('@tiptap/pm/model').Node } | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'tableRow' && node.attrs.investmentProduct?.id === id) {
      found = { pos, node };
      return false;
    }
    return true;
  });
  return found as { pos: number; node: import('@tiptap/pm/model').Node } | null;
}

export function confirmProductRemoval(product: InvestmentProduct) {
  return confirmAction({
    title: 'Product verwijderen?',
    description: `“${product.productName || 'Naamloos product'}” gaat uit de offerte. Je kunt dit ongedaan maken met ⌘Z / Ctrl+Z.`,
    confirmLabel: 'Verwijderen',
    destructive: true,
  });
}

export function updateInvestmentProduct(
  editor: Editor,
  product: InvestmentProduct,
  separateHistory = false
) {
  const target = findInvestmentProduct(editor, product.id);
  if (!target) return;
  const pricingType =
    target.node.childCount === 4 ? 'hourly' : target.node.childCount === 1 ? 'fixed' : 'recurring';
  const $pos = editor.state.doc.resolve(target.pos);
  let currency = 'EUR';
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === 'investmentGroupNode')
      currency = $pos.node(depth).attrs.currency || 'EUR';
  }
  const json = investmentProductRow(product, pricingType, currency);
  json.content = json.content?.map((cell, index) => ({
    ...cell,
    attrs: target.node.child(index).attrs,
  }));
  const tr = (separateHistory ? closeHistory(editor.state.tr) : editor.state.tr).replaceWith(
    target.pos,
    target.pos + target.node.nodeSize,
    editor.schema.nodeFromJSON(json)
  );
  if (
    editor.state.selection.from > target.pos &&
    editor.state.selection.from < target.pos + target.node.nodeSize
  ) {
    tr.setSelection(TextSelection.create(tr.doc, target.pos + 3));
  }
  editor.view.dispatch(tr);
}

export function duplicateInvestmentProduct(editor: Editor, id: string) {
  const target = findInvestmentProduct(editor, id);
  if (!target) return;
  // PM's toJSON shares attrs with the source node; clone before assigning an ID.
  const json = structuredClone(target.node.toJSON());
  json.attrs.investmentProduct.id = crypto.randomUUID();
  editor.view.dispatch(
    closeHistory(editor.state.tr).insert(
      target.pos + target.node.nodeSize,
      editor.schema.nodeFromJSON(json)
    )
  );
}
